import { useParams, useSearchParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { BackLink } from '@/components/layout/BackLink';
import { cn } from '@/lib/utils';
import { useControlPosture } from '@/api/hooks/useCompliance';
import {
  PANEL, AssessmentCard, FunctionsCard, RequirementsCard, ControlHeading,
} from '@/features/compliance/controls/control-detail';

/**
 * CONTROL — the full-page detail route (CC Increment 1 S4).
 *
 * The durable destination for deep work on a single control. Today it renders the sections
 * the canonical spine can support; questions, assignments, evidence, policies and activity
 * arrive as further cards in the same stack when their models exist, without a redesign.
 *
 * ⚠ ROUTE SHAPE. This deliberately sits at /compliance/controls/detail/:implementationId
 * rather than /compliance/controls/:id — the legacy pol_ tracker already owns
 * /compliance/controls/:frameworkId, and a bare id segment would be swallowed by it.
 * The static "detail" segment outranks the dynamic one, so the two coexist unambiguously.
 *
 * ⚠ Returning to the list preserves the caller's search, filters and selection: they travel
 * in the query string, so Back restores the exact working set rather than a reset page.
 */
export function ControlFullDetailPage() {
  const { implementationId } = useParams<{ implementationId: string }>();
  const [params] = useSearchParams();
  const { data, isLoading, isError } = useControlPosture();

  // Carry the list's working state back with us, minus nothing.
  const back = params.toString() ? `/compliance/controls?${params.toString()}` : '/compliance/controls';

  if (isLoading) {
    return (
      <div className="flex flex-col gap-[14px]">
        <Skeleton className="h-[18px] w-[150px]" />
        <Skeleton className="h-[92px] rounded-[10px]" />
        <Skeleton className="h-[320px] rounded-[10px]" />
      </div>
    );
  }

  const row = data?.controls.find((c) => c.implementationId === implementationId) ?? null;

  // ⚠ ABSENT vs BROKEN, and a third case: a load failure, a control that is not in this
  // tenant's workspace, and an empty workspace are three different facts.
  if (isError || !row) {
    return (
      <div className="flex flex-col gap-[14px]">
        <BackLink to={back} label="Controls" />
        <div className={cn(PANEL, 'flex flex-col items-center gap-[7px] px-6 py-[52px] text-center')}>
          <span className="text-[13px] font-semibold text-text-muted">
            {isError ? 'This control could not be loaded' : 'Control not found in your workspace'}
          </span>
          <span className="max-w-[46ch] text-[12px] leading-[1.6] text-text-faint">
            {isError
              ? 'This is a problem retrieving your data, not a statement about it.'
              : 'It may not be required by any framework enabled for this view.'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[14px]">
      <BackLink to={back} label="Controls" />

      <header className={cn(PANEL, 'px-[16px] py-[14px]')}>
        <ControlHeading row={row} large />
      </header>

      {/* Two columns on wide screens so the future workflow/evidence cards have somewhere to
          go without pushing the assessment below the fold. */}
      <div className="grid items-start gap-[12px] xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
        <div className="flex flex-col gap-[12px]">
          <RequirementsCard row={row} />
        </div>
        <div className="flex flex-col gap-[12px]">
          <AssessmentCard row={row} />
          <FunctionsCard row={row} />
        </div>
      </div>
    </div>
  );
}
